/**
 * storeHealth — a tiny framework-agnostic pub/sub bus for surfacing
 * storage-layer problems that React error boundaries cannot catch.
 *
 * The store adapters (Firestore subscription failures, localStorage
 * quota overflows, corrupted blobs) run outside React's render path, so
 * a thrown error there never reaches an ErrorBoundary. Instead they
 * `emitStoreHealth(...)` here, and `StoreHealthToaster` (mounted inside
 * the ToastProvider) bridges those events to a visible toast — so the
 * operator sees "sync failed" / "storage full" instead of silence.
 *
 * No React, no dependency: just a Set of listeners.
 */

import type { CollectionName } from './types';

export type StoreHealthLevel = 'error' | 'warning';

export interface StoreHealthEvent {
  level: StoreHealthLevel;
  /** Which collection, or the storage layer itself. */
  scope: CollectionName | 'storage';
  /** Short, operator-facing message. */
  message: string;
  /** Optional second line / technical detail. */
  detail?: string;
}

type Listener = (evt: StoreHealthEvent) => void;

const listeners = new Set<Listener>();

/** Broadcast a storage-health event to all subscribers. */
export function emitStoreHealth(evt: StoreHealthEvent): void {
  listeners.forEach((fn) => {
    try {
      fn(evt);
    } catch {
      // A broken listener must never break the emitter.
    }
  });
}

/** Subscribe to storage-health events. Returns an unsubscribe fn. */
export function onStoreHealth(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Thrown by the local adapter's `write()` when `localStorage` is full
 * (`QuotaExceededError`). Typed so a calling mutation can catch it
 * specifically rather than treating it as a generic failure.
 */
export class StoreQuotaError extends Error {
  readonly scope: CollectionName | 'storage';
  constructor(scope: CollectionName | 'storage', message: string) {
    super(message);
    this.name = 'StoreQuotaError';
    this.scope = scope;
  }
}
